import React, { useEffect, useState } from "react";
import apiClient from "../../lib/api";
import { useI18n } from "../../i18n";
import { BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { TrendingUp, Package, Users, AlertTriangle, ShoppingCart } from "lucide-react";

const Stat = ({ label, value, testid, icon: Icon }) => (
  <div className="p-6 border border-black/10 bg-white" data-testid={testid}>
    <div className="flex items-center justify-between">
      <div className="font-label text-ink-muted">{label}</div>
      {Icon && <Icon className="w-4 h-4 text-terracotta" />}
    </div>
    <div className="font-display text-3xl md:text-4xl mt-3 tracking-tighter">{value}</div>
  </div>
);

const AdminOverview = () => {
  const { t, lang } = useI18n();
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    Promise.all([
      apiClient.get("/admin/stats"),
      apiClient.get("/admin/orders"),
    ]).then(([s, o]) => {
      setStats(s.data);
      setRecent(o.data.slice(0, 8));
    });
  }, []);

  if (!stats) return <div className="text-ink-muted">Loading…</div>;

  return (
    <div data-testid="admin-overview-page">
      <div className="flex items-baseline justify-between mb-8">
        <h1 className="font-display text-4xl md:text-5xl tracking-tighter font-light">{lang === "nl" ? "Overzicht" : "Overview"}</h1>
        <div className="font-label text-ink-muted text-xs">{new Date().toLocaleDateString(lang === "nl" ? "nl-NL" : "en-GB", { weekday: "long", day: "numeric", month: "long" })}</div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-10">
        <Stat testid="stat-revenue" label={t("revenue")} value={`€${stats.revenue.toFixed(2)}`} icon={TrendingUp} />
        <Stat testid="stat-orders" label={t("orders")} value={stats.total_orders} icon={ShoppingCart} />
        <Stat testid="stat-customers" label={t("customers")} value={stats.total_customers} icon={Users} />
        <Stat testid="stat-products" label={t("products")} value={stats.total_products} icon={Package} />
        <Stat testid="stat-low-stock" label={t("low_stock")} value={stats.low_stock} icon={AlertTriangle} />
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        <div className="md:col-span-2 border border-black/10 bg-white p-6">
          <div className="font-label text-ink-muted mb-4">{lang === "nl" ? "Omzet — laatste 14 dagen" : "Sales — last 14 days"}</div>
          <div className="h-72">
            <ResponsiveContainer>
              <BarChart data={stats.sales_series}>
                <CartesianGrid stroke="#eee" vertical={false} />
                <XAxis dataKey="day" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip />
                <Bar dataKey="revenue" fill="#C86B52" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="border border-black/10 bg-white p-6">
          <div className="font-label text-ink-muted mb-4">{lang === "nl" ? "Recente bestellingen" : "Recent orders"}</div>
          <div className="space-y-3 max-h-72 overflow-y-auto">
            {recent.length === 0 && <div className="text-ink-muted text-sm">—</div>}
            {recent.map((o) => (
              <div key={o.order_id} className="flex items-center justify-between text-sm pb-2 border-b border-black/5 last:border-0">
                <div>
                  <div className="font-display">{o.order_id}</div>
                  <div className="text-xs text-ink-muted">{o.email}</div>
                </div>
                <div className="text-right">
                  <div className="font-medium">€{o.grand_total.toFixed(2)}</div>
                  <div className="font-label text-[10px] text-ink-muted">{o.status}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminOverview;
